import React, { createContext, useContext, useState } from 'react';

// Define the types for message types (e.g., error, success, info, etc.)
type MessageType = 'error' | 'success' | 'info' | 'warning';

// Define the structure of a message
interface Message {
  type: MessageType;
  content: string;
  visible: boolean;
  color?: string; // Optional color property
  timeout?: number; // Optional timeout property (in milliseconds)
}

interface NewPromptContext {
  prompt: string;
  response: string;
}

interface UtilityContextProps {
  modals: Record<string, boolean>;
  setModal: (modalName: string, isOpen: boolean) => void;
  loaders: Record<string, boolean>;
  setLoader: (loaderName: string, isLoading: boolean) => void;
  view: string;
  setView: React.Dispatch<React.SetStateAction<string>>;
  message: Message;
  showMessage: (
    type: MessageType,
    content: string,
    options?: { color?: string; timeout?: number } // Optional options object
  ) => void;
  hideMessage: () => void;
  // new prompt context state and setter
  newPromptData: NewPromptContext;
  setNewPromptData: React.Dispatch<React.SetStateAction<NewPromptContext>>;
}

const UtilityContext = createContext<UtilityContextProps | undefined>(
  undefined
);

export const UtilityContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [view, setView] = useState('Diff');
  const [modals, setModals] = useState<Record<string, boolean>>({});
  const [loaders, setLoaders] = useState<Record<string, boolean>>({});
  const [message, setMessage] = useState<Message>({
    type: 'info', // Default type
    content: '', // Default content
    visible: false, // Initially hidden
    color: undefined, // Default color (optional)
    timeout: undefined, // Default timeout (optional)
  });

  const [newPromptData, setNewPromptData] = useState<NewPromptContext>({
    prompt: '',
    response: '',
  });

  // Function to set a modal's visibility
  const setModal = (modalName: string, isOpen: boolean) => {
    setModals((prevModals) => ({
      ...prevModals,
      [modalName]: isOpen,
    }));
  };

  // Function to set a loader's visibility
  const setLoader = (loaderName: string, isLoading: boolean) => {
    setLoaders((prevLoaders) => ({
      ...prevLoaders,
      [loaderName]: isLoading,
    }));
  };

  // Function to show a message
  const showMessage = (
    type: MessageType,
    content: string,
    options?: { color?: string; timeout?: number } // Optional options
  ) => {
    const { color, timeout } = options || {};

    setMessage({
      type,
      content,
      visible: true,
      color, // Set the color (if provided)
      timeout, // Set the timeout (if provided)
    });

    // Automatically hide the message after the specified timeout
    if (timeout) {
      setTimeout(() => {
        hideMessage();
      }, timeout);
    }
  };

  // Function to hide the message
  const hideMessage = () => {
    setMessage((prevMessage) => ({
      ...prevMessage,
      visible: false,
    }));
  };

  const value = {
    modals,
    setModal,
    view,
    setView,
    loaders,
    setLoader,
    message,
    showMessage,
    hideMessage,
    newPromptData,
    setNewPromptData,
  };

  return (
    <UtilityContext.Provider value={value}>{children}</UtilityContext.Provider>
  );
};

export const useUtilityContext = (): UtilityContextProps => {
  const context = useContext(UtilityContext);
  if (!context)
    throw new Error(
      'useUtilityContext must be used within a UtilityContextProvider'
    );
  return context;
};
